
import { useState } from 'react';
import React from 'react';

export default function HomePage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [quoteItems, setQuoteItems] = useState<Array<{id: number, name: string, price: string, quantity: number, specs?: string}>>([]);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'All Products' },
    { id: 'stainless', name: 'Stainless Steel' },
    { id: 'aluminum', name: 'Aluminum' },
    { id: 'copper', name: 'Copper' },
    { id: 'brass', name: 'Brass' },
    { id: 'carbon', name: 'Carbon Steel' },
    { id: 'titanium', name: 'Titanium' },
    { id: 'nickel', name: 'Nickel Alloys' }
  ];

  const handleQuoteSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    // Add quote items to form data
    if (quoteItems.length > 0) {
      const quoteItemsText = quoteItems.map(item => 
        `${item.name} - ${item.price} (Qty: ${item.quantity})`
      ).join('\n');
      formData.append('quoteItems', quoteItemsText);
    }
    
    // Validate textarea length
    const additionalInfo = formData.get('additionalInfo') as string;
    if (additionalInfo && additionalInfo.length > 500) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d43d33epguuav66rfvp0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        form.reset();
        setQuoteItems([]);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePayQuote = async () => {
    if (quoteItems.length === 0) return;

    setIsProcessingPayment(true);

    try {
      // Get customer info from form
      const form = document.getElementById('quote-form') as HTMLFormElement;
      const formData = new FormData(form);
      
      const customerInfo = {
        firstName: formData.get('firstName') as string,
        lastName: formData.get('lastName') as string,
        email: formData.get('email') as string,
        phone: formData.get('phone') as string,
        company: formData.get('company') as string,
      };

      // Validate required fields
      if (!customerInfo.firstName || !customerInfo.lastName || !customerInfo.email) {
        alert('Please fill in all required customer information before proceeding with payment.');
        setIsProcessingPayment(false);
        return;
      }

      // Create payment session
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-quote-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          quoteItems: quoteItems,
          customerInfo: customerInfo,
        }),
      });

      const result = await response.json();

      if (result.success && result.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = result.checkout_url;
      } else {
        throw new Error(result.error || 'Failed to create payment session');
      }
    } catch (error) {
      console.error('Payment error:', error);
      alert('There was an error processing your payment. Please try again or contact us directly.');
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const addToQuote = (product: {id: number, name: string, price: string, specs?: string}) => {
    const existingItem = quoteItems.find(item => item.id === product.id);
    if (existingItem) {
      setQuoteItems(quoteItems.map(item => 
        item.id === product.id 
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setQuoteItems([...quoteItems, { ...product, quantity: 1 }]);
    }
    setShowQuoteModal(true);
    setTimeout(() => setShowQuoteModal(false), 2000);
  };

  const removeFromQuote = (productId: number) => {
    setQuoteItems(quoteItems.filter(item => item.id !== productId));
  };

  const updateQuantity = (productId: number, quantity: number) => {
    if (quantity <= 0) {
      removeFromQuote(productId);
    } else {
      setQuoteItems(quoteItems.map(item => 
        item.id === productId 
          ? { ...item, quantity }
          : item
      ));
    }
  };

  const products = [
    {
      id: 1,
      name: 'Stainless Steel Wire 316L',
      category: 'stainless',
      price: '$12.50/lb',
      image: 'https://public.readdy.ai/ai/img_res/70f7f10311e0e6fe1105070ee14f082c.jpg',
      specs: '0.032" diameter, 316L grade'
    },
    {
      id: 2,
      name: 'Stainless Steel Wire Spool 316L',
      category: 'stainless',
      price: '$15.75/lb',
      image: 'https://static.readdy.ai/image/86d54ddcda63029da005b85ca6e54f68/66b092cee5644a97a7212a8dfea5bebc.jpeg',
      specs: '0.020" diameter, 316L marine grade'
    },
    {
      id: 3,
      name: 'Aluminum Sheet 6061-T6',
      category: 'aluminum',
      price: '$8.75/sq ft',
      image: 'https://readdy.ai/api/search-image?query=Polished%20aluminum%20metal%20sheet%20with%20clean%20edges%20on%20industrial%20workbench%2C%20bright%20metallic%20surface%20reflecting%20light%2C%20professional%20manufacturing%20environment%2C%20silver%20metallic%20background&width=400&height=300&seq=2&orientation=landscape',
      specs: '1/8" thick, 4x8 sheets'
    },
    {
      id: 4,
      name: 'Copper Tubing Type L',
      category: 'copper',
      price: '$15.20/ft',
      image: 'https://readdy.ai/api/search-image?query=Bright%20copper%20metal%20tubing%20coiled%20professionally%20on%20industrial%20surface%2C%20warm%20copper%20color%20gleaming%20under%20professional%20lighting%2C%20clean%20metallic%20background%2C%20commercial%20grade%20piping&width=400&height=300&seq=3&orientation=landscape',
      specs: '3/4" diameter, Type L grade'
    },
    {
      id: 5,
      name: 'Brass Rod C360',
      category: 'brass',
      price: '$22.80/ft',
      image: 'https://readdy.ai/api/search-image?query=Golden%20brass%20metal%20rod%20with%20polished%20surface%20on%20professional%20workbench%2C%20warm%20golden%20metallic%20finish%20%2C%20industrial%20manufacturing%20setting%2C%20clean%20metallic%20background&width=400&height=300&seq=4&orientation=landscape',
      specs: '1" diameter, C360 alloy'
    },
    {
      id: 6,
      name: 'Stainless Steel Plate 304',
      category: 'stainless',
      price: '$18.90/sq ft',
      image: 'https://readdy.ai/api/search-image?query=Thick%20stainless%20steel%20metal%20plate%20with%20brushed%20finish%20on%20industrial%20table%2C%20reflective%20metallic%20surface%2C%20professional%20workshop%20environment%2C%20silver%20metallic%20background&width=400&height=300&seq=5&orientation=landscape',
      specs: '1/4" thick, 304 grade'
    },
    {
      id: 7,
      name: 'Aluminum Angle 6063',
      category: 'aluminum',
      price: '$6.45/ft',
      image: 'https://readdy.ai/api/search-image?query=Aluminum%20angle%20metal%20extrusion%20with%20clean%20edges%20on%20industrial%20surface%2C%20bright%20silver%20metallic%20finish%2C%20professional%20manufacturing%20environment%2C%20metallic%20background&width=400&height=300&seq=6&orientation=landscape',
      specs: '2" x 2" x 1/8" angle'
    },
    {
      id: 8,
      name: 'Carbon Steel Round Bar A36',
      category: 'carbon',
      price: '$4.25/ft',
      image: 'https://readdy.ai/api/search-image?query=Dark%20carbon%20steel%20round%20bar%20with%20matte%20finish%20on%20industrial%20workbench%2C%20professional%20lighting%20clean%20metallic%20background%2C%20commercial%20grade%20steel%20rod&width=400&height=300&seq=9&orientation=landscape',
      specs: '1" diameter, A36 grade'
    },
    {
      id: 9,
      name: 'Titanium Sheet Grade 2',
      category: 'titanium',
      price: '$125.00/sq ft',
      image: 'https://readdy.ai/api/search-image?query=Premium%20titanium%20metal%20sheet%20with%20distinctive%20gray%20metallic%20finish%20on%20professional%20surface%2C%20aerospace%20grade%20material%2C%20clean%20industrial%20background%2C%20high-end%20metal%20product&width=400&height=300&seq=10&orientation=landscape',
      specs: '0.125" thick, Grade 2'
    },
    {
      id: 10,
      name: 'Nickel Alloy 625 Rod',
      category: 'nickel',
      price: '$89.50/ft',
      image: 'https://readdy.ai/api/search-image?query=High-grade%20nickel%20alloy%20rod%20with%20polished%20metallic%20surface%20on%20industrial%20table%2C%20premium%20aerospace%20material%2C%20clean%20metallic%20background%2C%20professional%20product%20photography&width=400&height=300&seq=11&orientation=landscape',
      specs: '0.75" diameter, Inconel 625'
    },
    {
      id: 11,
      name: 'Stainless Steel Tube 316',
      category: 'stainless',
      price: '$28.75/ft',
      image: 'https://readdy.ai/api/search-image?query=Polished%20stainless%20steel%20tube%20with%20seamless%20finish%20on%20professional%20workbench%2C%20bright%20reflective%20surface%20industrial%20manufacturing%20environment%2C%20silver%20metallic%20background&width=400&height=300&seq=12&orientation=landscape',
      specs: '2" OD x 0.125" wall, 316 grade'
    },
    {
      id: 12,
      name: 'Aluminum Plate 5083',
      category: 'aluminum',
      price: '$12.30/sq ft',
      image: 'https://readdy.ai/api/search-image?query=Thick%20aluminum%20plate%20with%20marine%20grade%20finish%20on%20industrial%20surface%2C%20bright%20metallic%20appearance%20professional%20workshop%20setting%2C%20clean%20aluminum%20background&width=400&height=300&seq=13&orientation=landscape',
      specs: '1/2" thick, 5083 marine grade'
    },
    {
      id: 13,
      name: 'Copper Sheet C110',
      category: 'copper',
      price: '$24.60/sq ft',
      image: 'https://readdy.ai/api/search-image?query=Bright%20copper%20metal%20sheet%20with%20polished%20surface%20on%20professional%20table%2C%20warm%20copper%20color%20reflecting%20light%20industrial%20setting%20clean%20metallic%20background&width=400&height=300&seq=14&orientation=landscape',
      specs: '1/8" thick, C110 grade'
    },
    {
      id: 14,
      name: 'Brass Plate C260',
      category: 'brass',
      price: '$31.40/sq ft',
      image: 'https://readdy.ai/api/search-image?query=Golden%20brass%20metal%20plate%20with%20polished%20finish%20on%20industrial%20workbench%2C%20warm%20golden%20metallic%20surface%20professional%20manufacturing%20environment%20clean%20brass%20background&width=400&height=300&seq=15&orientation=landscape',
      specs: '1/4" thick, C260 cartridge brass'
    },
    {
      id: 15,
      name: 'Carbon Steel Flat Bar 1018',
      category: 'carbon',
      price: '$3.85/ft',
      image: 'https://readdy.ai/api/search-image?query=Carbon%20steel%20flat%20bar%20with%20hot%20rolled%20finish%20on%20industrial%20surface%2C%20dark%20metallic%20appearance%20professional%20workshop%20environment%20clean%20steel%20background&width=400&height=300&seq=16&orientation=landscape',
      specs: '2" x 1/4" flat bar, 1018 grade'
    },
    {
      id: 16,
      name: 'Titanium Rod Grade 5',
      category: 'titanium',
      price: '$156.00/ft',
      image: 'https://readdy.ai/api/search-image?query=Premium%20titanium%20rod%20with%20aerospace%20grade%20finish%20on%20professional%20surface%2C%20distinctive%20gray%20metallic%20color%20high-end%20industrial%20setting%20clean%20titanium%20background&width=400&height=300&seq=17&orientation=landscape',
      specs: '1" diameter, Ti-6Al-4V Grade 5'
    },
    {
      id: 17,
      name: 'Nickel Alloy 718 Plate',
      category: 'nickel',
      price: '$198.75/sq ft',
      image: 'https://readdy.ai/api/search-image?query=High-performance%20nickel%20alloy%20plate%20with%20premium%20finish%20on%20industrial%20table%2C%20aerospace%20grade%20material%20professional%20lighting%20clean%20metallic%20background&width=400&height=300&seq=18&orientation=landscape',
      specs: '1/2" thick, Inconel 718'
    },
    {
      id: 18,
      name: 'Stainless Steel Hexagon 17-4PH',
      category: 'stainless',
      price: '$45.20/ft',
      image: 'https://readdy.ai/api/search-image?query=Precision%20stainless%20steel%20hexagon%20bar%20with%20machined%20finish%20on%20professional%20workbench%2C%20bright%20metallic%20surface%20industrial%20manufacturing%20setting%20silver%20background&width=400&height=300&seq=19&orientation=landscape',
      specs: '1.5" hex, 17-4PH precipitation hardening'
    },
    {
      id: 19,
      name: 'Aluminum Tubing 6061-T6',
      category: 'aluminum',
      price: '$11.85/ft',
      image: 'https://readdy.ai/api/search-image?query=Seamless%20aluminum%20tubing%20with%20polished%20finish%20on%20industrial%20surface%2C%20bright%20silver%20metallic%20appearance%20professional%20workshop%20environment%20clean%20aluminum%20background&width=400&height=300&seq=20&orientation=landscape',
      specs: '1.5" OD x 0.125" wall, 6061-T6'
    },
    {
      id: 20,
      name: 'Custom Stainless Steel Brackets',
      category: 'stainless',
      price: '$35.00/each',
      image: 'https://readdy.ai/api/search-image?query=Custom%20fabricated%20stainless%20steel%20mounting%20brackets%20with%20precision%20holes%20and%20bends%2C%20professional%20welding%20finish%20industrial%20workbench%20setting%20clean%20metallic%20background%20specialty%20manufactured%20parts&width=400&height=300&seq=21&orientation=landscape',
      specs: 'Custom fabricated, 304SS, various sizes'
    },
    {
      id: 21,
      name: 'Aluminum Heat Sinks',
      category: 'aluminum',
      price: '$42.50/each',
      image: 'https://readdy.ai/api/search-image?query=Precision%20machined%20aluminum%20heat%20sink%20with%20cooling%20fins%20and%20mounting%20holes%20professional%20CNC%20finish%20industrial%20manufacturing%20environment%20clean%20aluminum%20background%20custom%20engineered%20cooling%20solution&width=400&height=300&seq=22&orientation=landscape',
      specs: 'CNC machined, 6061-T6, custom designs'
    },
    {
      id: 22,
      name: 'Copper Bus Bars',
      category: 'copper',
      price: '$68.90/each',
      image: 'https://readdy.ai/api/search-image?query=Heavy%20duty%20copper%20electrical%20bus%20bars%20with%20precision%20drilled%20mounting%20holes%20bright%20copper%20finish%20professional%20electrical%20manufacturing%20setting%20clean%20metallic%20background%20custom%20electrical%20components&width=400&height=300&seq=23&orientation=landscape',
      specs: 'Custom lengths, C101 copper, electrical grade'
    },
    {
      id: 23,
      name: 'Brass Fittings & Connectors',
      category: 'brass',
      price: '$28.75/each',
      image: 'https://readdy.ai/api/search-image?query=Precision%20machined%20brass%20pipe%20fittings%20and%20connectors%20with%20threaded%20ends%20golden%20brass%20finish%20professional%20plumbing%20manufacturing%20environment%20clean%20brass%20background%20custom%20hydraulic%20components&width=400&height=300&seq=24&orientation=landscape',
      specs: 'Threaded fittings, C360 brass, custom specs'
    },
    {
      id: 24,
      name: 'Carbon Steel Weldments',
      category: 'carbon',
      price: '$125.00/each',
      image: 'https://readdy.ai/api/search-image?query=Heavy%20duty%20carbon%20steel%20welded%20assemblies%20with%20structural%20joints%20and%20reinforcement%20plates%20professional%20welding%20finish%20industrial%20fabrication%20shop%20clean%20steel%20background%20custom%20structural%20components&width=400&height=300&seq=25&orientation=landscape',
      specs: 'Welded assemblies, A36 steel, custom designs'
    },
    {
      id: 25,
      name: 'Titanium Aerospace Components',
      category: 'titanium',
      price: '$485.00/each',
      image: 'https://readdy.ai/api/search-image?query=Precision%20machined%20titanium%20aerospace%20components%20with%20complex%20geometry%20and%20tight%20tolerances%20premium%20titanium%20finish%20high-tech%20manufacturing%20facility%20clean%20titanium%20background%20aerospace%20grade%20parts&width=400&height=300&seq=26&orientation=landscape',
      specs: 'CNC machined, Grade 5 Ti, aerospace certified'
    },
    {
      id: 26,
      name: 'Nickel Alloy Pressure Vessels',
      category: 'nickel',
      price: '$1,250.00/each',
      image: 'https://readdy.ai/api/search-image?query=Custom%20fabricated%20nickel%20alloy%20pressure%20vessel%20with%20welded%20seams%20and%20inspection%20ports%20high-performance%20alloy%20finish%20specialized%20manufacturing%20facility%20clean%20metallic%20background%20industrial%20pressure%20equipment&width=400&height=300&seq=27&orientation=landscape',
      specs: 'Welded vessels, Inconel 625, ASME certified'
    },
    {
      id: 27,
      name: 'Stainless Steel Food Grade Tanks',
      category: 'stainless',
      price: '$890.00/each',
      image: 'https://readdy.ai/api/search-image?query=Polished%20stainless%20steel%20food%20grade%20storage%20tank%20with%20sanitary%20fittings%20and%20smooth%20interior%20finish%20bright%20reflective%20surface%20food%20processing%20facility%20clean%20stainless%20background%20hygienic%20manufacturing&width=400&height=300&seq=28&orientation=landscape',
      specs: '316L SS, sanitary finish, FDA compliant'
    },
    {
      id: 28,
      name: 'Aluminum Architectural Panels',
      category: 'aluminum',
      price: '$65.00/sq ft',
      image: 'https://readdy.ai/api/search-image?query=Custom%20aluminum%20architectural%20facade%20panels%20with%20decorative%20perforations%20and%20anodized%20finish%20modern%20building%20exterior%20professional%20architectural%20setting%20clean%20aluminum%20background%20contemporary%20design%20elements&width=600&height=400&seq=29&orientation=landscape',
      specs: 'Perforated panels, 5052 aluminum, anodized'
    },
    {
      id: 29,
      name: 'Copper Roofing Systems',
      category: 'copper',
      price: '$18.50/sq ft',
      image: 'https://readdy.ai/api/search-image?query=Premium%20copper%20roofing%20sheets%20with%20standing%20seam%20profiles%20and%20weathered%20patina%20finish%20architectural%20roofing%20application%20professional%20construction%20setting%20clean%20copper%20background%20traditional%20craftsmanship&width=400&height=300&seq=30&orientation=landscape',
      specs: 'Standing seam, C110 copper, architectural grade'
    },
    {
      id: 30,
      name: 'Nickel 200 Wire',
      category: 'nickel',
      price: '$45.80/lb',
      image: 'https://readdy.ai/api/search-image?query=High-purity%20nickel%20wire%20spool%20with%20bright%20metallic%20finish%20on%20industrial%20workbench%2C%20professional%20lighting%20clean%20metallic%20background%2C%20commercial%20grade%20pure%20nickel%20wire%20for%20electrical%20applications&width=400&height=300&seq=31&orientation=landscape',
      specs: '0.025" diameter, 99.6% pure nickel'
    },
    {
      id: 31,
      name: 'Inconel 625 Welding Wire',
      category: 'nickel',
      price: '$125.50/lb',
      image: 'https://readdy.ai/api/search-image?query=Premium%20Inconel%20welding%20wire%20spool%20with%20aerospace%20grade%20finish%20on%20professional%20welding%20station%2C%20high-performance%20alloy%20wire%20clean%20industrial%20background%2C%20specialized%20welding%20materials&width=400&height=300&seq=32&orientation=landscape',
      specs: '0.035" diameter, AWS ER NiCrMo-3'
    },
    {
      id: 32,
      name: 'Inconel 718 Wire',
      category: 'nickel',
      price: '$156.75/lb',
      image: 'https://readdy.ai/api/search-image?query=Aerospace%20grade%20Inconel%20718%20wire%20with%20precision%20finish%20on%20high-tech%20manufacturing%20surface%2C%20premium%20superalloy%20wire%20clean%20metallic%20background%2C%20advanced%20materials%20for%20critical%20applications&width=400&height=300&seq=33&orientation=landscape',
      specs: '0.020" diameter, precipitation hardening'
    },
    {
      id: 33,
      name: 'Monel 400 Wire',
      category: 'nickel',
      price: '$68.90/lb',
      image: 'https://readdy.ai/api/search-image?query=Corrosion-resistant%20Monel%20400%20wire%20spool%20with%20nickel-copper%20alloy%20finish%20on%20marine%20grade%20workbench%2C%20professional%20lighting%20clean%20metallic%20background%2C%20marine%20industry%20wire%20materials&width=400&height=300&seq=34&orientation=landscape',
      specs: '0.032" diameter, nickel-copper alloy'
    },
    {
      id: 34,
      name: 'Hastelloy C-276 Wire',
      category: 'nickel',
      price: '$189.25/lb',
      image: 'https://readdy.ai/api/search-image?query=Ultra-high%20performance%20Hastelloy%20C-276%20wire%20with%20chemical%20resistant%20finish%20on%20specialized%20manufacturing%20surface%2C%20premium%20superalloy%20wire%20clean%20industrial%20background%2C%20chemical%20processing%20materials&width=400&height=300&seq=35&orientation=landscape',
      specs: '0.030" diameter, chemical resistant'
    }
  ];

  const filteredProducts = selectedCategory === 'all' 
    ? products 
    : products.filter(product => product.category === selectedCategory);

  return (
    <div className="min-h-screen bg-white">
      {/* Quote Modal */}
      {showQuoteModal && (
        <div className="fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-pulse">
          <div className="flex items-center space-x-2">
            <i className="ri-check-line text-xl"></i>
            <span>Added to quote!</span>
          </div>
        </div>
      )}

      {/* Quote Summary Button */}
      {quoteItems.length > 0 && (
        <div className="fixed bottom-4 right-4 z-40">
          <button
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-full shadow-lg flex items-center space-x-2 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-file-list-3-line text-xl"></i>
            <span>Quote ({quoteItems.length})</span>
          </button>
        </div>
      )}

      {/* Header */}
      <header className="bg-gray-900 text-white">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                <img 
                  src="https://static.readdy.ai/image/86d54ddcda63029da005b85ca6e54f68/b5a6c0d9bc70d8400f36872c2465ceb8.png"
                  alt="Hugh Muir Metals Logo"
                  className="h-12 w-auto"
                />
                <div>
                  <h1 className="text-xl font-bold" style={{ fontFamily: '"Pacifico", serif' }}>
                    Hugh Muir
                  </h1>
                  <span className="text-gray-300 text-sm">Metals</span>
                </div>
              </div>
            </div>
            <nav className="hidden md:flex space-x-8">
              <a href="#products" className="hover:text-blue-400 transition-colors cursor-pointer">Products</a>
              <a href="#services" className="hover:text-blue-400 transition-colors cursor-pointer">Services</a>
              <a href="#about" className="hover:text-blue-400 transition-colors cursor-pointer">About</a>
              <a 
                href="#contact" 
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="hover:text-blue-400 transition-colors cursor-pointer"
              >
                Contact
              </a>
            </nav>
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
              >
                Get Quote
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section 
        className="relative h-screen flex items-center justify-center text-white"
        style={{
          backgroundImage: 'url(https://readdy.ai/api/search-image?query=Modern%20industrial%20metal%20manufacturing%20facility%20with%20stainless%20steel%20equipment%20and%20machinery%2C%20professional%20lighting%20clean%20metallic%20surfaces%20%2C%20industrial%20workspace%20with%20metal%20materials%20ensure%20left%20side%20has%20darker%20overlay%20for%20text%20readability&width=1920&height=1080&seq=7&orientation=landscape)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-black/50"></div>
        <div className="relative z-10 container mx-auto px-6">
          <div className="w-full max-w-2xl">
            <h2 className="text-5xl font-bold mb-6 leading-tight">
              Premium Metal Solutions for Every Industry
            </h2>
            <p className="text-xl mb-8 text-gray-200">
              High-quality stainless steel, aluminum, copper, and brass materials with precision cutting and custom fabrication services. Trusted by manufacturers worldwide.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button 
                onClick={() => document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-blue-600 hover:bg-blue-700 px-8 py-4 rounded-lg text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
              >
                Browse Products
              </button>
              <button 
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                className="border-2 border-white hover:bg-white hover:text-gray-900 px-8 py-4 rounded-lg text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
              >
                Request Quote
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-gray-900 mb-4">Our Metal Products</h3>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover our extensive range of high-quality metals including stainless steel wire, aluminum sheets, copper tubing, and brass components for all your industrial needs.
            </p>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-6 py-3 rounded-full font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                  selectedCategory === category.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>

          {/* Product Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-product-shop>
            {filteredProducts.map((product) => (
              <div key={product.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="h-64 overflow-hidden">
                  <img 
                    src={product.image} 
                    alt={product.name}
                    className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <h4 className="text-xl font-bold text-gray-900 mb-2">{product.name}</h4>
                  <p className="text-gray-600 mb-3">{product.specs}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold text-blue-600">{product.price}</span>
                    <button 
                      onClick={() => addToQuote(product)}
                      className="bg-gray-900 hover:bg-gray-800 text-white px-6 py-2 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Add to Quote
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-gray-900 mb-4">Our Services</h3>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Beyond supplying premium metals, we offer comprehensive fabrication and finishing services to meet your exact specifications.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center p-8 bg-gray-50 rounded-xl">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-scissors-cut-line text-2xl text-white"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-4">Precision Cutting</h4>
              <p className="text-gray-600">
                Custom cutting services with laser precision for exact dimensions and clean edges on all metal types.
              </p>
            </div>

            <div className="text-center p-8 bg-gray-50 rounded-xl">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-hammer-line text-2xl text-white"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-4">Custom Fabrication</h4>
              <p className="text-gray-600">
                Expert fabrication services including welding, bending, and forming to create custom metal components.
              </p>
            </div>

            <div className="text-center p-8 bg-gray-50 rounded-xl">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-paint-brush-line text-2xl text-white"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-4">Surface Finishing</h4>
              <p className="text-gray-600">
                Professional finishing services including polishing, coating, and treatment for enhanced durability.
              </p>
            </div>

            <div className="text-center p-8 bg-gray-50 rounded-xl">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-truck-line text-2xl text-white"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-4">Fast Delivery</h4>
              <p className="text-gray-600">
                Reliable shipping and delivery services with tracking for orders of any size across the country.
              </p>
            </div>

            <div className="text-center p-8 bg-gray-50 rounded-xl">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-customer-service-2-line text-2xl text-white"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-4">Expert Support</h4>
              <p className="text-gray-600">
                Technical consultation and material selection guidance from our experienced metal specialists.
              </p>
            </div>

            <div className="text-center p-8 bg-gray-50 rounded-xl">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-6 mb-6">
                <i className="ri-medal-line text-2xl text-white"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-4">Quality Guarantee</h4>
              <p className="text-gray-600">
                All materials come with quality certifications and our satisfaction guarantee for peace of mind.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-gray-900 text-white">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-4xl font-bold mb-6">About Hugh Muir Metals</h3>
              <p className="text-xl text-gray-300 mb-6">
                With over 25 years of experience in the metal industry, Hugh Muir Metals has become a trusted supplier for manufacturers, contractors, and businesses across the United States.
              </p>
              <p className="text-gray-300 mb-8">
                We specialize in providing high-quality stainless steel, aluminum, copper, and brass materials with precision cutting and custom fabrication services. Our commitment to quality, competitive pricing, and exceptional customer service has made us the preferred choice for metal solutions.
              </p>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <div className="text-3xl font-bold text-blue-400 mb-2">25+</div>
                  <div className="text-gray-300">Years Experience</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-blue-400 mb-2">10,000+</div>
                  <div className="text-gray-300">Satisfied Customers</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-blue-400 mb-2">500+</div>
                  <div className="text-gray-300">Metal Products</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-blue-400 mb-2">99.8%</div>
                  <div className="text-gray-300">Quality Rating</div>
                </div>
              </div>
            </div>
            <div className="h-96 overflow-hidden rounded-xl">
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20metal%20manufacturing%20warehouse%20with%20organized%20inventory%20of%20steel%20aluminum%20copper%20materials%2C%20industrial%20setting%20with%20proper%20lighting%2C%20workers%20in%20safety%20gear%2C%20clean%20organized%20facility&width=600&height=400&seq=8&orientation=landscape"
                alt="SurePure Metal Solutions Facility"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Get Your Metal Quote Today</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Contact Hugh Muir Metals for competitive pricing on premium metal products. 
              Our expert team provides personalized quotes and technical support for all your metal needs.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Quote Request Form */}
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Request a Quote</h3>
              
              {/* Quote Items Summary */}
              {quoteItems.length > 0 && (
                <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-gray-900">Selected Items:</h4>
                    <button
                      onClick={handlePayQuote}
                      disabled={isProcessingPayment}
                      className={`px-4 py-2 rounded-lg font-semibold whitespace-nowrap cursor-pointer transition-colors ${
                        isProcessingPayment 
                          ? 'bg-gray-400 cursor-not-allowed text-white' 
                          : 'bg-green-600 hover:bg-green-700 text-white'
                      }`}
                    >
                      {isProcessingPayment ? 'Processing...' : 'Pay Now'}
                    </button>
                  </div>
                  <div className="space-y-2">
                    {quoteItems.map((item) => (
                      <div key={item.id} className="flex items-center justify-between text-sm">
                        <span className="text-gray-700">{item.name}</span>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            className="w-6 h-6 bg-gray-200 hover:bg-gray-300 rounded-full flex items-center justify-center cursor-pointer"
                          >
                            <i className="ri-subtract-line text-xs"></i>
                          </button>
                          <span className="w-8 text-center">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            className="w-6 h-6 bg-gray-200 hover:bg-gray-300 rounded-full flex items-center justify-center cursor-pointer"
                          >
                            <i className="ri-add-line text-xs"></i>
                          </button>
                          <button
                            onClick={() => removeFromQuote(item.id)}
                            className="w-6 h-6 bg-red-100 hover:bg-red-200 text-red-600 rounded-full flex items-center justify-center cursor-pointer ml-2"
                          >
                            <i className="ri-close-line text-xs"></i>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <form 
                id="quote-form"
                data-readdy-form
                action="https://readdy.ai/api/form/d43d33epguuav66rfvp0"
                method="POST"
                onSubmit={handleQuoteSubmit}
                className="space-y-6"
              >
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Enter your first name"
                    />
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Enter your last name"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="Your company name"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="metalType" className="block text-sm font-medium text-gray-700 mb-2">
                      Metal Type *
                    </label>
                    <div className="relative">
                      <select
                        id="metalType"
                        name="metalType"
                        required
                        className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm appearance-none bg-white"
                      >
                        <option value="">Select metal type</option>
                        <option value="stainless-steel">Stainless Steel</option>
                        <option value="aluminum">Aluminum</option>
                        <option value="copper">Copper</option>
                        <option value="brass">Brass</option>
                        <option value="carbon-steel">Carbon Steel</option>
                        <option value="titanium">Titanium</option>
                        <option value="nickel-alloys">Nickel Alloys</option>
                        <option value="other">Other</option>
                      </select>
                      <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                    </div>
                  </div>
                  <div>
                    <label htmlFor="grade" className="block text-sm font-medium text-gray-700 mb-2">
                      Grade/Alloy
                    </label>
                    <input
                      type="text"
                      id="grade"
                      name="grade"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="e.g., 316L, 6061-T6, C110"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="form" className="block text-sm font-medium text-gray-700 mb-2">
                      Form/Shape *
                    </label>
                    <div className="relative">
                      <select
                        id="form"
                        name="form"
                        required
                        className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm appearance-none bg-white"
                      >
                        <option value="">Select form</option>
                        <option value="sheet">Sheet</option>
                        <option value="plate">Plate</option>
                        <option value="tube">Tube</option>
                        <option value="pipe">Pipe</option>
                        <option value="rod">Rod</option>
                        <option value="bar">Bar</option>
                        <option value="angle">Angle</option>
                        <option value="wire">Wire</option>
                        <option value="hexagon">Hexagon</option>
                        <option value="other">Other</option>
                      </select>
                      <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                    </div>
                  </div>
                  <div>
                    <label htmlFor="quantity" className="block text-sm font-medium text-gray-700 mb-2">
                      Quantity *
                    </label>
                    <input
                      type="text"
                      id="quantity"
                      name="quantity"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="e.g., 100 lbs, 50 sheets, 200 ft"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="additionalInfo" className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Information
                  </label>
                  <textarea
                    id="additionalInfo"
                    name="additionalInfo"
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="Tell us more about your project, special requirements, or delivery needs..."
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
                </div>

                <div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full py-4 px-6 rounded-lg text-lg font-semibold transition-colors whitespace-nowrap ${
                      isSubmitting 
                        ? 'bg-gray-400 cursor-not-allowed' 
                        : 'bg-blue-600 hover:bg-blue-700 text-white cursor-pointer'
                    }`}
                  >
                    {isSubmitting ? 'Sending...' : 'Submit Quote Request'}
                  </button>
                  
                  {submitStatus === 'success' && (
                    <div className="mt-4 p-4 bg-green-100 text-green-700 rounded-lg">
                      Thank you! Your quote request has been sent successfully. We'll contact you soon.
                    </div>
                  )}
                  
                  {submitStatus === 'error' && (
                    <div className="mt-4 p-4 bg-red-100 text-red-700 rounded-lg">
                      There was an error submitting your request. Please try again or contact us directly.
                    </div>
                  )}
                </div>
              </form>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              <div className="bg-white rounded-lg shadow-lg p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Contact Information</h3>
                <div className="space-y-6">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <i className="ri-map-pin-line text-xl text-blue-600"></i>
                    </div>
                    <div className="ml-4">
                      <h4 className="text-lg font-semibold text-gray-900 mb-1">Address</h4>
                      <p className="text-gray-600">
                        1 Tiffany Point, Suite G 10<br />
                        Bloomington, IL 60618<br />
                        United States
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <i className="ri-phone-line text-xl text-blue-600"></i>
                    </div>
                    <div className="ml-4">
                      <h4 className="text-lg font-semibold text-gray-900 mb-1">Phone</h4>
                      <p className="text-gray-600">
                        (773) 850-0803
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <i className="ri-mail-line text-xl text-blue-600"></i>
                    </div>
                    <div className="ml-4">
                      <h4 className="text-lg font-semibold text-gray-900 mb-1">Email</h4>
                      <p className="text-gray-600">
                        info@hughmuir.com
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <i className="ri-time-line text-xl text-blue-600"></i>
                    </div>
                    <div className="ml-4">
                      <h4 className="text-lg font-semibold text-gray-900 mb-1">Business Hours</h4>
                      <p className="text-gray-600">
                        Monday - Friday: 8:00 AM - 6:00 PM<br />
                        Saturday: 9:00 AM - 1:00 PM<br />
                        Sunday: Closed
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-lg p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Why Choose Us</h3>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start">
                    <i className="ri-check-line text-green-500 mt-1 mr-3"></i>
                    <span>25+ years industry experience</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-green-500 mt-1 mr-3"></i>
                    <span>Premium quality materials</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-green-500 mt-1 mr-3"></i>
                    <span>Competitive wholesale pricing</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-green-500 mt-1 mr-3"></i>
                    <span>Custom fabrication capabilities</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-green-500 mt-1 mr-3"></i>
                    <span>Nationwide delivery service</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <img 
                  src="https://static.readdy.ai/image/86d54ddcda63029da005b85ca6e54f68/b5a6c0d9bc70d8400f36872c2465ceb8.png"
                  alt="Hugh Muir Metals Logo"
                  className="h-10 w-auto"
                />
                <div>
                  <h4 className="text-lg font-bold" style={{ fontFamily: '"Pacifico", serif' }}>Hugh Muir</h4>
                  <span className="text-gray-400 text-sm">Metals</span>
                </div>
              </div>
              <p className="text-gray-400 mb-4">
                Premium metal solutions for manufacturers, contractors, and businesses since 1998.
              </p>
              <div className="flex space-x-4">
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <i className="ri-facebook-fill text-xl"></i>
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <i className="ri-twitter-fill text-xl"></i>
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <i className="ri-linkedin-fill text-xl"></i>
                </a>
              </div>
            </div>

            <div>
              <h5 className="text-lg font-semibold mb-4">Products</h5>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Stainless Steel</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Aluminum</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Copper</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Brass</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Carbon Steel</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Titanium</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Nickel Alloys</a></li>
              </ul>
            </div>

            <div>
              <h5 className="text-lg font-semibold mb-4">Services</h5>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Precision Cutting</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Custom Fabrication</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Surface Finishing</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Technical Support</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Material Certification</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Consultation Services</a></li>
              </ul>
            </div>

            <div>
              <h5 className="text-lg font-semibold mb-4">Quick Links</h5>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#products" className="hover:text-white transition-colors">Product Catalog</a></li>
                <li><a href="#services" className="hover:text-white transition-colors">Our Services</a></li>
                <li><a href="#about" className="hover:text-white transition-colors">About Us</a></li>
                <li><a href="#contact" className="hover:text-white transition-colors">Contact</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Request Quote</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Order Status</a></li>
              </ul>
            </div>
          </div>

          {/* Legal Disclaimer Section */}
          <div className="border-t border-gray-800 mt-12 pt-8">
            <div className="bg-gray-800 rounded-lg p-6 mb-6">
              <h5 className="text-lg font-semibold mb-4 text-white">Legal Disclaimer</h5>
              <div className="text-sm text-gray-300 space-y-3">
                <p>
                  <strong>Material Specifications:</strong> All material specifications, dimensions, and properties listed on this website are approximate and for reference purposes only. Actual specifications may vary. Customers should verify all specifications and suitability for their specific applications before ordering.
                </p>
                <p>
                  <strong>Pricing:</strong> All prices shown are subject to change without notice and are based on current market conditions. Final pricing will be confirmed in written quotes. Prices do not include shipping, handling, taxes, or other applicable fees unless specifically stated.
                </p>
                <p>
                  <strong>Product Availability:</strong> Product availability is subject to change. Hugh Muir Metals reserves the right to discontinue products or modify specifications without prior notice. Custom orders may have extended lead times.
                </p>
                <p>
                  <strong>Limitation of Liability:</strong> Hugh Muir Metals shall not be liable for any indirect, incidental, special, or consequential damages arising from the use of our products or services. Our liability is limited to the replacement or refund of defective materials.
                </p>
                <p>
                  <strong>Technical Information:</strong> Technical data and recommendations provided are based on our experience and testing. Customers are responsible for determining the suitability of materials for their specific applications and compliance with applicable codes and standards.
                </p>
                <p>
                  <strong>Intellectual Property:</strong> All content on this website, including images, text, logos, and designs, is the property of Hugh Muir Metals and is protected by copyright and trademark laws. Unauthorized use is prohibited.
                </p>
                <p>
                  <strong>Terms of Sale:</strong> All sales are subject to our standard terms and conditions. By placing an order, customers agree to these terms. Payment terms, delivery schedules, and return policies are outlined in our formal quotations and invoices.
                </p>
              </div>
            </div>
          </div>

          <div className="text-center text-gray-400">
            <p>&copy; 2023 Hugh Muir Metals. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-white transition-colors">Website Builder</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
